import { t } from '@store/I18nStore'
export type ReportDatePreset = 'today' | 'last7' | 'last30'

function pad2(value: number): string {
  return String(value).padStart(2, '0')
}

export function toLocalDayKey(date: Date): string {
  const y = date.getFullYear()
  const m = pad2(date.getMonth() + 1)
  const d = pad2(date.getDate())
  return `${y}-${m}-${d}`
}

export function parseLocalDayKey(dayKey: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayKey)
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const date = new Date(year, month - 1, day)
  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null
  }
  return date
}

export function getLocalDayKeyFromIso(iso: string): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return toLocalDayKey(date)
}

export function getStartOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)
}

export function addLocalDays(date: Date, days: number): Date {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

export function resolveLocalPresetRange(preset: ReportDatePreset): { dateFrom: string; dateTo: string } {
  const now = new Date()
  const startToday = getStartOfLocalDay(now)
  const endExclusive = addLocalDays(startToday, 1)
  if (preset === 'today') {
    return { dateFrom: startToday.toISOString(), dateTo: endExclusive.toISOString() }
  }
  const days = preset === 'last30' ? 30 : 7
  const start = addLocalDays(startToday, -days + 1)
  return { dateFrom: start.toISOString(), dateTo: endExclusive.toISOString() }
}

/** Инклюзивный календарный диапазон YYYY-MM-DD для пресетов. */
export function resolveLocalPresetDayKeys(
  preset: ReportDatePreset,
  now = new Date(),
): { dateFrom: string; dateTo: string } {
  const startToday = getStartOfLocalDay(now)
  const todayKey = toLocalDayKey(startToday)
  if (preset === 'today') {
    return { dateFrom: todayKey, dateTo: todayKey }
  }
  const days = preset === 'last30' ? 30 : 7
  const start = addLocalDays(startToday, -days + 1)
  return { dateFrom: toLocalDayKey(start), dateTo: todayKey }
}

export function formatDayKeyRu(dayKey: string): string {
  const date = parseLocalDayKey(dayKey)
  if (!date) return dayKey
  const d = pad2(date.getDate())
  const m = pad2(date.getMonth() + 1)
  return `${d}.${m}.${date.getFullYear()}`
}

/** Подпись триггера: «Сегодня» / «Вчера» / «21.07.2026». */
export function formatWorktimeDayTriggerLabel(dayKey: string, now = new Date()): string {
  const todayKey = toLocalDayKey(now)
  const yesterdayKey = toLocalDayKey(addLocalDays(getStartOfLocalDay(now), -1))
  if (dayKey === todayKey) return t('time.today')
  if (dayKey === yesterdayKey) return t('time.yesterday')
  return formatDayKeyRu(dayKey)
}

/** Подпись диапазона: «Сегодня» / «15.07.2026 – 21.07.2026». */
export function formatDateRangeTriggerLabel(
  dateFrom: string,
  dateTo: string,
  now = new Date(),
): string {
  if (dateFrom === dateTo) return formatWorktimeDayTriggerLabel(dateFrom, now)
  return `${formatDayKeyRu(dateFrom)} – ${formatDayKeyRu(dateTo)}`
}

export function getTimeZoneOffsetLabel(date = new Date()): string {
  const minutes = -date.getTimezoneOffset()
  const sign = minutes >= 0 ? '+' : '-'
  const abs = Math.abs(minutes)
  const hours = pad2(Math.floor(abs / 60))
  const mins = pad2(abs % 60)
  return `UTC${sign}${hours}:${mins}`
}

export function getUserTimeZoneLabel(): string {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Local'
  return `${zone} (${getTimeZoneOffsetLabel()})`
}

type TimeShortUnit = 'day' | 'hour' | 'min' | 'month' | 'sec' | 'second'

export function timeShort(unit: TimeShortUnit): string {
  return t(`time.short.${unit}`)
}

type TimeUnitKind = 'day' | 'hour' | 'month' | 'week'

export function timeUnit(kind: TimeUnitKind, count: number): string {
  return t(`time.units.${kind}`, { count })
}

export function monthsShort(months: number): string {
  return `${months} ${timeShort('month')}`
}

/** Plural unit word only, e.g. «дней» from `time.units.day`. */
export function timeUnitWord(kind: TimeUnitKind, count: number): string {
  return timeUnit(kind, count).replace(String(count), '').trim()
}
